import { BaseDialog } from '@/components/BaseDialog'
import { DatePicker } from '@/components/DatePicker'
import { ImageUpload } from '@/components/ImageUpload'
import { LogTimeline } from '@/components/LogTimeline'
import { UserSelect } from '@/components/UserSelect'
import { PriorityBadge } from '@/components/badges/PriorityBadge'
import { RepairStatusBadge } from '@/components/badges/RepairStatusBadge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { useGetUsers } from '@/features/users/hooks/useUser'
import { UserRole } from '@/features/users/types/user'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import { useFileUploadField } from '@/hooks/useFileUpload'
import { useUI } from '@/hooks/useUI'
import { isApiError } from '@/utils/error'
import { showError, showSuccess } from '@/utils/toast'
import { Calendar, Camera, CheckCircle2, Clock, Hash, Loader2, User, Wrench } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useAcknowledgeRepair,
  useAssignRepair,
  useCompleteRepair,
  useGetRepair,
  useGetRepairLogs,
  useUpdateRepairStatus,
} from '../hooks/useRepair'

const toDateTimeOffset = (date: string) => {
  if (!date) return undefined

  const now = new Date()
  const [year, month, day] = date.split('-').map(Number)
  return new Date(year, month - 1, day, now.getHours(), now.getMinutes(), now.getSeconds()).toISOString()
}

export function RepairDetailDialog({
  repairId,
  open,
  onOpenChange,
}: {
  repairId: string | null
  open: boolean
  onOpenChange: (o: boolean) => void
}) {
  const { t } = useTranslation()
  const { isMaintainer, isAdmin } = useAuth()
  const { repairStatuses } = useUI()
  const { formatDateTime } = useDateFormatter()
  const { data: repair, isLoading } = useGetRepair(repairId, open)
  const { data: repairLogs = [], isLoading: isLoadingLogs } = useGetRepairLogs(repairId, open)
  const { data: maintainers = [] } = useGetUsers(UserRole.Maintainer, open)
  const acknowledgeRepair = useAcknowledgeRepair()
  const assignRepair = useAssignRepair()
  const updateRepairStatus = useUpdateRepairStatus()
  const completeRepair = useCompleteRepair()
  const [note, setNote] = useState('')
  const [estDate, setEstDate] = useState('')
  const [newStatus, setNewStatus] = useState('InProgress')
  const [actionImageIds, setActionImageIds] = useState<string[]>([])
  const [assignedTechnicianId, setAssignedTechnicianId] = useState('')
  const actionImageField = useFileUploadField({
    value: actionImageIds,
    onChange: setActionImageIds,
    maxFiles: 5,
    onUploadError: error => showError(isApiError(error) ? error.message : t('common.uploadImageError')),
  })

  useEffect(() => {
    if (!open) {
      setNote('')
      setEstDate('')
      setNewStatus('InProgress')
      setActionImageIds([])
      setAssignedTechnicianId('')
      return
    }

    if (repair?.estimatedCompletion) {
      setEstDate(repair.estimatedCompletion.slice(0, 10))
    }
    if (repair?.technicianId) {
      setAssignedTechnicianId(repair.technicianId)
    }
  }, [open, repair?.estimatedCompletion, repair?.technicianId, repairId])

  const updateStatusOptions = useMemo(() => {
    const allowedStatuses = new Set(['InProgress', 'WaitingParts'])
    return repairStatuses?.filter(status => allowedStatuses.has(status.value))
  }, [repairStatuses])

  const isMutating =
    acknowledgeRepair.isPending || assignRepair.isPending || updateRepairStatus.isPending || completeRepair.isPending
  const isSubmittingAction = isMutating || actionImageField.isUploading

  const runAction = async (action: () => Promise<unknown>, successMessage: string) => {
    try {
      await action()
      showSuccess(successMessage)
      setNote('')
      setActionImageIds([])
      onOpenChange(false)
    } catch (error) {
      showError(isApiError(error) ? error.message : t('repairs.actionError'))
    }
  }

  const itemLabel = repair?.item
    ? `${repair.item.productName || repair.item.productName || '-'} (${repair.item.serialNumber || '-'})`
    : '-'
  const reporterLabel = repair?.reporter
    ? `${repair.reporter.name}${repair.reporterDepartment?.name ? ` (${repair.reporterDepartment.name})` : ''}`
    : '-'

  return (
    <BaseDialog
      open={open}
      onOpenChange={onOpenChange}
      contentTestId="repair-detail-dialog"
      header={
        <>
          <Wrench className="size-4" />
          {repair?.title ?? t('repairs.detailTitle')}
        </>
      }
    >
      {isLoading ? (
        <div className="flex min-h-32 items-center justify-center">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      ) : !repair ? (
        <div className="py-8 text-center text-sm text-muted-foreground">{t('repairs.notFound')}</div>
      ) : (
        <div className="flex flex-col gap-4 pt-2">
          <div className="flex items-center gap-2">
            <RepairStatusBadge status={repair.status} />
            <PriorityBadge priority={repair.priority} />
          </div>

          <div className="flex flex-col gap-2 rounded-lg border border-border/30 bg-secondary/20 p-3">
            <div className="flex items-center gap-2 text-xs">
              <Hash className="size-3 text-muted-foreground" />
              <span className="text-muted-foreground">{t('common.equipment')}:</span>
              <span className="font-medium text-foreground">{itemLabel}</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <User className="size-3 text-muted-foreground" />
              <span className="text-muted-foreground">{t('repairs.reportedBy')}:</span>
              <span className="text-foreground">{reporterLabel}</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <Clock className="size-3 text-muted-foreground" />
              <span className="text-muted-foreground">{t('repairs.reportedDate')}:</span>
              <span className="text-foreground">
                {formatDateTime(repair.dateCreated, {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            </div>
            {repair.technician && (
              <div className="flex items-center gap-2 text-xs">
                <Wrench className="size-3 text-muted-foreground" />
                <span className="text-muted-foreground">{t('repairs.technician')}:</span>
                <span className="text-foreground">{repair.technician.name}</span>
              </div>
            )}
            {repair.estimatedCompletion && (
              <div className="flex items-center gap-2 text-xs">
                <Calendar className="size-3 text-muted-foreground" />
                <span className="text-muted-foreground">{t('repairs.estimatedCompletion')}:</span>
                <span className="text-foreground">
                  {formatDateTime(repair.estimatedCompletion, {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </div>
            )}
            <p className="text-xs text-muted-foreground">{repair.description}</p>
          </div>

          {repair.technicianNote && (
            <div className="rounded-lg border border-info/30 bg-info/5 p-3">
              <span className="text-[10px] text-muted-foreground">{t('repairs.technicianNote')}:</span>
              <p className="text-xs text-foreground">{repair.technicianNote}</p>
            </div>
          )}

          <div className="flex flex-col gap-2">
            <span className="text-xs font-medium text-foreground">{t('repairs.history')}</span>
            <LogTimeline entries={repairLogs} isLoading={isLoadingLogs} hiddenActions={['ItemStatusChanged']} />
          </div>

          {isAdmin && repair.status === 'Open' && (
            <div className="flex flex-col gap-3 border-t border-border/30 pt-3">
              <Label className="text-xs font-medium">
                {repair.technicianId ? t('repairs.reassign') : t('repairs.assign')}
              </Label>
              <UserSelect
                testId="repair-detail-assign"
                users={maintainers}
                value={assignedTechnicianId}
                onValueChange={setAssignedTechnicianId}
                placeholder={t('repairs.selectTechnician')}
              />
              <Button
                data-testid="repair-detail-assign-button"
                className="text-xs"
                disabled={!assignedTechnicianId || assignRepair.isPending}
                onClick={() =>
                  runAction(
                    () =>
                      assignRepair.mutateAsync({
                        id: repair.id,
                        payload: { technicianId: assignedTechnicianId },
                      }),
                    t('repairs.assignSuccess'),
                  )
                }
              >
                {assignRepair.isPending ? (
                  <Loader2 className="mr-1 size-3 animate-spin" />
                ) : (
                  <CheckCircle2 className="mr-1 size-3" />
                )}
                {repair.technicianId ? t('repairs.reassign') : t('repairs.assign')}
              </Button>
            </div>
          )}

          {isMaintainer && repair.status === 'Open' && (
            <div className="flex flex-col gap-3 border-t border-border/30 pt-3">
              <Label className="text-xs font-medium">{t('repairs.acknowledgeSection')}</Label>
              <Button
                data-testid="repair-detail-acknowledge"
                className="text-xs"
                disabled={isMutating}
                onClick={() =>
                  runAction(() => acknowledgeRepair.mutateAsync({ id: repair.id }), t('repairs.acknowledgeSuccess'))
                }
              >
                {acknowledgeRepair.isPending ? (
                  <Loader2 className="mr-1 size-3 animate-spin" />
                ) : (
                  <CheckCircle2 className="mr-1 size-3" />
                )}
                {t('repairs.acknowledge')}
              </Button>
            </div>
          )}

          {(isMaintainer || isAdmin) && ['Acknowledged', 'InProgress', 'WaitingParts'].includes(repair.status) && (
            <div className="flex flex-col gap-3 border-t border-border/30 pt-3">
              <Label className="text-xs font-medium">{t('repairs.updateStatus')}</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger data-testid="repair-detail-update-status" className="bg-secondary/50 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card text-foreground">
                  {updateStatusOptions?.map(status => (
                    <SelectItem data-testid={`repair-detail-update-status-option-${status.value}`} key={status.value} value={status.value}>
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <DatePicker testId="repair-detail-estimated-date" value={estDate} onChange={setEstDate} placeholder={t('repairs.estimatedCompletion')} />
              <Textarea
                data-testid="repair-detail-note"
                value={note}
                onChange={e => setNote(e.target.value)}
                className="bg-secondary/50 text-xs"
                rows={2}
                placeholder={t('repairs.notePlaceholder')}
              />
              <div className="flex flex-col gap-1.5">
                <Label className="flex items-center gap-1 text-xs">
                  <Camera className="size-3" />
                  {t('repairs.attachImages')}
                </Label>
                <ImageUpload
                  files={actionImageField.files}
                  imageIds={actionImageField.imageIds}
                  onChange={actionImageField.handleChange}
                  onRemoveExisting={actionImageField.handleRemoveExisting}
                  maxImages={5}
                />
                {actionImageField.isUploading ? (
                  <p className="text-[11px] text-muted-foreground">
                    {t('common.uploadingImages')}
                    {actionImageField.uploadProgress ? ` ${actionImageField.uploadProgress}%` : '...'}
                  </p>
                ) : null}
              </div>
              <div className="flex gap-2">
                <Button
                  data-testid="repair-detail-update-status-button"
                  variant="outline"
                  className="flex-1 text-xs"
                  disabled={isSubmittingAction}
                  onClick={() =>
                    runAction(
                      () =>
                        updateRepairStatus.mutateAsync({
                          id: repair.id,
                          payload: {
                            status: newStatus,
                            estimatedCompletion: toDateTimeOffset(estDate),
                            technicianNote: note || undefined,
                            imageIds: actionImageIds,
                          },
                        }),
                      t('repairs.updateSuccess'),
                    )
                  }
                >
                  {updateRepairStatus.isPending && <Loader2 className="mr-1 size-3 animate-spin" />}
                  {t('repairs.updateStatus')}
                </Button>
                {['InProgress', 'WaitingParts'].includes(repair.status) && (
                  <Button
                    data-testid="repair-detail-complete"
                    className="flex-1 text-xs"
                    disabled={isSubmittingAction}
                    onClick={() =>
                      runAction(
                        () =>
                          completeRepair.mutateAsync({
                            id: repair.id,
                            payload: {
                              technicianNote: note || t('repairs.completeDefaultNote'),
                              imageIds: actionImageIds,
                            },
                          }),
                        t('repairs.completeSuccess'),
                      )
                    }
                  >
                    {completeRepair.isPending ? (
                      <Loader2 className="mr-1 size-3 animate-spin" />
                    ) : (
                      <CheckCircle2 className="mr-1 size-3" />
                    )}
                    {t('repairs.complete')}
                  </Button>
                )}
              </div>
            </div>
          )}

          {repair.status === 'Completed' && (
            <div className="rounded-lg border border-success/30 bg-success/5 p-3">
              <p className="flex items-center gap-2 text-xs text-success">
                <CheckCircle2 className="size-3" />
                {t('repairs.completedAt', {
                  date: formatDateTime(repair.completedAt, {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  }),
                })}
              </p>
            </div>
          )}
        </div>
      )}
    </BaseDialog>
  )
}
